import React, { useState, useRef } from 'react';
import { FaChartBar, FaNetworkWired, FaUsers, FaTags } from 'react-icons/fa';

function Home({ onContactClick }) {
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const videoRef = useRef(null);
  const modalVideoRef = useRef(null);

  const features = [
    {
      icon: <FaChartBar size={40} />,
      title: "Comprehensive Monitoring",
      description: "Easily connect and monitor your audio media for key performance metrics related to applications/media players, location, devices, and demographics."
    },
    {
      icon: <FaNetworkWired size={40} />,
      title: "Cross-Platform Analytics",
      description: "Compare engagement across platforms and request custom reports specific to your organization. Add weights, calculations, outside data sets, and bespoke visuals to get the most out of your audio data."
    },
    {
      icon: <FaUsers size={40} />,
      title: "User-Level Access",
      description: "Set up user-level access for your organization to filter account performance data for specific stakeholders."
    },
    {
      icon: <FaTags size={40} />,
      title: "Advanced Account Organization",
      description: "Add tags and user groups to better aggregate data for reporting and organize accounts."
    }
  ];

  const openVideoModal = () => {
    setIsVideoModalOpen(true);
  };

  const closeVideoModal = () => {
    setIsVideoModalOpen(false);
  };

  const handleFullscreen = (videoElement) => {
    if (videoElement.requestFullscreen) {
      videoElement.requestFullscreen();
    } else if (videoElement.webkitRequestFullscreen) {
      videoElement.webkitRequestFullscreen();
    } else if (videoElement.mozRequestFullScreen) {
      videoElement.mozRequestFullScreen();
    } else if (videoElement.msRequestFullscreen) {
      videoElement.msRequestFullscreen();
    }
  };

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content-layout">
          <div className="hero-text">
            <div className="platform-icons">
              <img src="/Octicons-rss.svg" alt="RSS Icon" className="platform-icon" />
              <img src="/YouTube_full-color_icon_(2017).webp" alt="YouTube Icon" className="platform-icon" />
              <img src="/Spotify_1.png" alt="Spotify Icon" className="platform-icon" />
              <img src="/ITunes_logo.svg" alt="iTunes Icon" className="platform-icon" />
            </div>
            <h1>Enterprise Podcast Analytics</h1>
            <p className="hero-subtitle">
              RSS Insight is an enterprise podcast analytics platform geared towards large broadcasters and media entities
            </p>
            <p className="hero-description">
              Monitor your digital audio presence across RSS Feeds, YouTube, Spotify, and iTunes with comprehensive analytics and insights.
            </p>
            <div className="cta-buttons">
              <button onClick={onContactClick} className="cta-primary">Learn More</button>
            </div>
          </div>
          <div className="hero-demo-section">
            <div className="demo-container" onClick={openVideoModal}>
              <video 
                ref={videoRef}
                className="demo-video"
                autoPlay
                loop
                muted
                playsInline
                preload="auto"
                src="/rss_demo_clip3.mp4"
                onError={(e) => {
                  console.error('Video failed to load:', e);
                }}
              />
              <div className="demo-overlay">
                <div className="expand-icon">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <p className="expand-text">Click to expand</p>
              </div>
            </div>
            <p className="demo-caption">See RSS Insight in action</p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="section-header">
          <span className="section-tag">Key Features</span>
          <h2>Powerful analytics for enterprise podcast management</h2>
        </div>
        
        <div className="features-grid">
          {features.map((feature, index) => (
            <div key={index} className="feature-card">
              <div className="feature-icon">
                {feature.icon}
              </div>
              <h3>{feature.title}</h3>
              <p>{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Why It Matters Section */}
      <section className="why-matters-section">
        <div className="wim-content">
          <div className="wim-text">
            <span className="section-tag">Why It Matters</span>
            <h2>Unified analytics across all your podcast platforms</h2>
            <div className="feature-pillars">
              <div className="pillar">
                <h4>Multi-Platform Support</h4>
                <p>Track performance across RSS Feeds, YouTube, Spotify, and iTunes from a single dashboard</p>
              </div>
              <div className="pillar">
                <h4>Enterprise-Grade Security</h4>
                <p>User-level access controls and secure data management for your organization</p>
              </div>
              <div className="pillar">
                <h4>Custom Reporting</h4>
                <p>Request bespoke reports with custom calculations, weights, and visualizations</p>
              </div>
              <div className="pillar">
                <h4>Advanced Organization</h4>
                <p>Tag accounts and create user groups for streamlined data aggregation and reporting</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="final-cta-section">
        <div className="cta-content">
          <h2>Ready to transform your podcast analytics?</h2>
          <p>Get comprehensive insights across all your audio platforms</p>
          <button onClick={onContactClick} className="cta-primary large">Contact Us</button>
        </div>
      </section>

      {/* Video Modal */}
      {isVideoModalOpen && (
        <div className="video-modal-overlay" onClick={closeVideoModal}>
          <div className="video-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="video-modal-close" onClick={closeVideoModal}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <button 
              className="video-modal-fullscreen" 
              onClick={() => handleFullscreen(modalVideoRef.current)}
              title="Fullscreen"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <video 
              ref={modalVideoRef}
              className="video-modal-video"
              autoPlay
              loop
              muted
              playsInline
              controls
              src="/rss_demo_clip3.mp4"
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default Home;

